import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import api from '../../services/api';

const statusLabel = { online: 'Онлайн', offline: 'Офлайн', vacation: 'В отпуске' };

export default function EmployeeList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', source_uid: '', source_code: '', phone: '', password: '' });
  const [employees, setEmployees] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '100' });
        if (search) params.set('search', search);
        if (filterStatus) params.set('status', filterStatus);
        if (filterStore) params.set('store_id', filterStore);
        const [empRes, storesRes] = await Promise.allSettled([
          api.get(`/v1/employees?${params}`),
          api.get('/v1/stores'),
        ]);
        if (empRes.status === 'fulfilled' && empRes.value?.data) setEmployees(empRes.value.data);
        if (storesRes.status === 'fulfilled' && storesRes.value?.data) setStores(storesRes.value.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [search, filterStatus, filterStore]);

  const columns = [
    {
      key: 'name', label: 'Сотрудник',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Avatar name={row.name} size={36} />
            <span style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff',
              background: row.status === 'online' ? '#10B981' : row.status === 'vacation' ? '#F59E0B' : '#CBD5E1',
            }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{row.name}</div>
            <div style={{ fontSize: 12, color: '#94A3B8' }}>{row.phone || row.source_code}</div>
          </div>
        </div>
      )
    },
    { key: 'source_code', label: 'Код', render: v => <code style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748B', background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>{v}</code> },
    { key: 'position_name', label: 'Должность', render: (v, row) => {
      const name = v || (row.position && row.position.name);
      return name ? <Badge variant="primary">{name}</Badge> : <span style={{ color: '#CBD5E1' }}>&mdash;</span>;
    }},
    {
      key: 'store_name', label: 'Магазин',
      render: (v, row) => {
        const name = v || (row.store && row.store.name);
        return name ? <Badge variant="info">{name}</Badge> : <span style={{ color: '#CBD5E1' }}>&mdash;</span>;
      }
    },
    { key: 'status', label: 'Статус', render: v => <Badge variant={v || 'offline'} dot>{statusLabel[v] || 'Офлайн'}</Badge> },
    { key: 'tasks_completed', label: 'Задач', render: v => <span style={{ fontWeight: 700, color: '#009995' }}>{v || 0}</span> },
  ];

  const handleCreate = async () => {
    if (!form.name || !form.source_uid || !form.source_code) { toast('Заполните обязательные поля', 'error'); return; }
    try {
      await api.post('/v1/employees', form);
      toast(`Сотрудник ${form.name} создан`, 'success');
      setShowModal(false);
      setForm({ name: '', source_uid: '', source_code: '', phone: '', password: '' });
      const res = await api.get('/v1/employees?page=1&per_page=100');
      if (res?.data) setEmployees(res.data);
    } catch (e) {
      toast(e.message || 'Ошибка создания', 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Сотрудники</h1>
          <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 2, fontWeight: 500 }}>
            Всего: {employees.length} {loading && '(загрузка...)'}
          </p>
        </div>
        <Button icon={<UserPlus size={15} />} onClick={() => setShowModal(true)}>Добавить</Button>
      </div>

      <Card padding="0">
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid #F1F5F9',
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#F8FAFC', borderRadius: 10, padding: '8px 14px',
            flex: 1, minWidth: 180, border: '1.5px solid #E2E8F0',
          }}>
            <Search size={14} color="#94A3B8" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по имени / коду..."
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', fontFamily: 'inherit', color: '#0F172A' }} />
          </div>
          <select value={filterStore} onChange={e => setFilterStore(e.target.value)} style={{
            padding: '8px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10,
            fontSize: 13, color: '#334155', background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <option value="">Все магазины</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{
            padding: '8px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10,
            fontSize: 13, color: '#334155', background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <option value="">Все статусы</option>
            <option value="online">Онлайн</option>
            <option value="offline">Офлайн</option>
          </select>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8', fontSize: 13 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
            Загрузка...
          </div>
        ) : (
          <Table columns={columns} data={employees} onRowClick={row => navigate(`/employees/${row.id}`)} emptyText="Сотрудники не найдены" />
        )}
      </Card>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Добавить сотрудника">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="ФИО" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Иванов Иван" required />
          <Input label="Source UID" value={form.source_uid} onChange={e => setForm(f => ({ ...f, source_uid: e.target.value }))} placeholder="UUID из 1С" required />
          <Input label="Код (source_code)" value={form.source_code} onChange={e => setForm(f => ({ ...f, source_code: e.target.value }))} placeholder="SC-0001" required />
          <Input label="Телефон" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+998 90 000 00 00" />
          <Input label="Пароль" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Минимум 3 символа" />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Отмена</Button>
            <Button onClick={handleCreate}>Создать</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
